import { useCallback, useEffect, useState } from 'react'
import { grievanceRepository } from '../../data/repositories/GrievanceRepositoryImpl'
import { GrievanceRecord, GrievanceStatus } from '../../domain/entities/Grievance'

export type GrievanceRecordWithBuyer = GrievanceRecord

export const useGrievance = (initialLimit = 10) => {
  const [records, setRecords] = useState<GrievanceRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<GrievanceStatus | undefined>(undefined) // Default to all issues (undefined)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalItems, setTotalItems] = useState(0)

  const fetchGrievances = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await grievanceRepository.getGrievances({
        search: search || undefined,
        status,
        page,
        limit: initialLimit,
      })
      setRecords(res.records)
      setTotalPages(res.totalPages)
      setTotalItems(res.totalItems)
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to load grievances.')
      setRecords([])
      setTotalPages(1)
      setTotalItems(0)
    } finally {
      setLoading(false)
    }
  }, [search, status, page, initialLimit])

  useEffect(() => {
    fetchGrievances()
  }, [fetchGrievances])

  const delegateAm = async (id: string, amId: string, amName: string) => {
    setError(null)
    try {
      await grievanceRepository.assignAm(id, amId, amName)
      await fetchGrievances()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to assign Account Manager.')
      throw err
    }
  }

  const resolveIssue = async (id: string, notes: string) => {
    setError(null)
    try {
      await grievanceRepository.resolveGrievance(id, notes)
      await fetchGrievances()
    } catch (err: unknown) {
      const errorVal = err as Error
      setError(errorVal.message || 'Failed to resolve grievance.')
      throw err
    }
  }

  const resetFilters = () => {
    setSearch('')
    setStatus(undefined)
    setPage(1)
  }

  const handleSearchChange = (val: string) => {
    setSearch(val)
    setPage(1)
  }

  return {
    records,
    loading,
    error,
    search,
    setSearch: handleSearchChange,
    status,
    setStatus,
    page,
    setPage,
    totalPages,
    totalItems,
    delegateAm,
    resolveIssue,
    resetFilters,
    refresh: fetchGrievances,
  }
}
